/**
 * ==========================================
 * NEXT-DAY DELIVERY COUNTDOWN TIMER
 * ==========================================
 * 
 * Custom Web Component for next-day delivery countdown
 * 
 * Features:
 * - Real-time countdown to cutoff time (UK timezone)
 * - Respects active delivery days configuration
 * - Automatically updates when product variant changes
 * - Shows/hides based on stock status and timing
 * - Handles edge cases: past cutoff, inactive days, out of stock
 * 
 * Usage:
 * <next-day-delivery-timer
 *   data-cutoff-time="16:00"
 *   data-active-days="1,2,3,4,5"
 *   data-show-out-of-stock="false"
 *   data-countdown-label="Order in"
 *   data-cutoff-message="Order by {time} for next-day delivery"
 *   data-delivered-message="Next-day delivery not available">
 * </next-day-delivery-timer>
 * 
 * ==========================================
 */

class NextDayDeliveryTimer extends HTMLElement {
  constructor() {
    super();
    this.updateInterval = null;
    this.isVisible = true;
  }

  connectedCallback() {
    // Ensure initialization happens after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeTimer();
        this.setupVariantListener();
        this.startCountdown();
      });
    } else {
      this.initializeTimer();
      this.setupVariantListener();
      this.startCountdown();
    }
  }

  disconnectedCallback() {
    this.stopCountdown();
    this.removeVariantListener();
  }

  /**
   * Initialize timer on first load
   */
  initializeTimer() {
    this.cutoffTime = this.dataset.cutoffTime || '16:00';
    //this.activeDays = (this.dataset.activeDays || '1,2,3,4,5').split(',').map(d => parseInt(d, 10)).filter(Boolean);
    this.activeDays = (this.dataset.activeDays || '1,2,3,4,5')
  .split(',')
  .map(day => parseInt(day, 10))
  .filter(day => Number.isInteger(day) && day >= 0 && day <= 6);
    this.showWhenOutOfStock = this.dataset.showOutOfStock === 'true';
    this.countdownLabel = this.dataset.countdownLabel || 'Order in';
    this.cutoffMessage = this.dataset.cutoffMessage || 'Order by {time} for next-day delivery';
    this.deliveredMessage = this.dataset.deliveredMessage || 'Next-day delivery not available';

    this.countdownDisplay = this.querySelector('[data-countdown-display]');
    this.unavailableMessage = this.querySelector('[data-unavailable-message]');
    this.hoursEl = this.querySelector('[data-countdown-hours]');
    this.minutesEl = this.querySelector('[data-countdown-minutes]');
    this.secondsEl = this.querySelector('[data-countdown-seconds]');
    this.cutoffTextEl = this.querySelector('[data-cutoff-text]');
    this.variantAvailableEl = this.querySelector('[data-variant-available]');

    // Debug log
    console.log('NextDayDeliveryTimer initialized:', {
      cutoffTime: this.cutoffTime,
      activeDays: this.activeDays,
      hasCountdownDisplay: !!this.countdownDisplay,
      hasUnavailableMessage: !!this.unavailableMessage,
      hasHoursEl: !!this.hoursEl
    });

    if (this.unavailableMessage) {
      this.unavailableMessage.textContent = this.deliveredMessage;
    }

    // Validate elements exist
    if (!this.countdownDisplay) {
      console.warn('NextDayDeliveryTimer: [data-countdown-display] not found');
    }
    if (!this.unavailableMessage) {
      console.warn('NextDayDeliveryTimer: [data-unavailable-message] not found');
    }
    if (!this.hoursEl || !this.minutesEl || !this.secondsEl) {
      console.warn('NextDayDeliveryTimer: Timer elements missing', {
        hasHours: !!this.hoursEl,
        hasMinutes: !!this.minutesEl,
        hasSeconds: !!this.secondsEl
      });
    }

    // Initialize display regardless
    this.updateDisplay();
  }

  /**
   * Calculate time until cutoff in UK timezone
   * Returns: { hours, minutes, seconds, isPastCutoff, isInactiveDay }
   */
  calculateTimeUntilCutoff() {
    // Create date in UK timezone (Europe/London)
    const now = new Date();
    const ukTime = new Date(now.toLocaleString('en-GB', { timeZone: 'Europe/London' }));

    // Parse cutoff time
    const [cutoffHours, cutoffMinutes] = this.cutoffTime.split(':').map(Number);
    const cutoffToday = new Date(ukTime);
    cutoffToday.setHours(cutoffHours, cutoffMinutes, 0, 0);

    // Get current day of week (0 = Sunday, 1-6 = Mon-Sat)
    const currentDayOfWeek = ukTime.getDay();

    // Debug: Log time and day info
    console.log('NextDayDeliveryTimer.calculateTimeUntilCutoff():', {
      ukTime: ukTime.toString(),
      currentDayOfWeek,
      cutoffToday: cutoffToday.toString(),
      activeDays: this.activeDays,
      cutoffTime: this.cutoffTime
    });

    // Check if today is an active delivery day
    const isActiveDayToday = this.activeDays.includes(currentDayOfWeek);

    console.log('Day check:', {
      isActiveDayToday,
      ukTimeMillis: ukTime.getTime(),
      cutoffTodayMillis: cutoffToday.getTime(),
      isPastCutoff: ukTime >= cutoffToday
    });

    // If cutoff has passed today, check tomorrow
    if (ukTime >= cutoffToday) {
      console.log('Cutoff has passed today, finding next active day...');
      
      // Find next active delivery day
      let nextDay = new Date(cutoffToday);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(cutoffHours, cutoffMinutes, 0, 0);

      for (let i = 0; i < 14; i++) {
        const dayOfWeek = nextDay.getDay();
        if (this.activeDays.includes(dayOfWeek)) {
          const timeUntil = this.getTimeDifference(ukTime, nextDay);
          console.log('Found next active day:', { nextDay: nextDay.toString(), dayOfWeek, timeUntil });
          return {
            hours: timeUntil.hours,
            minutes: timeUntil.minutes,
            seconds: timeUntil.seconds,
            isPastCutoff: true,
            isInactiveDay: !isActiveDayToday,
            deliveryDay: this.getFormattedDate(nextDay),
            cutoffTime: this.cutoffTime
          };
        }
        nextDay.setDate(nextDay.getDate() + 1);
      }

      // No active days found in next 2 weeks
      console.log('No active days found in next 14 days');
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        isPastCutoff: true,
        isInactiveDay: true,
        isNoAvailableDays: true,
        cutoffTime: this.cutoffTime
      };
    }

    // Cutoff hasn't passed today and today is active
    if (isActiveDayToday) {
      const timeUntil = this.getTimeDifference(ukTime, cutoffToday);
      console.log('Today is active and cutoff not passed:', timeUntil);
      return {
        hours: timeUntil.hours,
        minutes: timeUntil.minutes,
        seconds: timeUntil.seconds,
        isPastCutoff: false,
        isInactiveDay: false,
        deliveryDay: this.getFormattedDate(new Date(ukTime.getTime() + 24 * 60 * 60 * 1000)),
        cutoffTime: this.cutoffTime
      };
    }

    // Today is inactive, find next active day
    console.log('Today is inactive, finding next active day...');
    let nextDay = new Date(cutoffToday);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(cutoffHours, cutoffMinutes, 0, 0);

    for (let i = 0; i < 14; i++) {
      const dayOfWeek = nextDay.getDay();
      if (this.activeDays.includes(dayOfWeek)) {
        const timeUntil = this.getTimeDifference(ukTime, nextDay);
        console.log('Found next active day:', { nextDay: nextDay.toString(), dayOfWeek, timeUntil });
        return {
          hours: timeUntil.hours,
          minutes: timeUntil.minutes,
          seconds: timeUntil.seconds,
          isPastCutoff: true,
          isInactiveDay: true,
          deliveryDay: this.getFormattedDate(nextDay),
          cutoffTime: this.cutoffTime
        };
      }
      nextDay.setDate(nextDay.getDate() + 1);
    }

    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
      isPastCutoff: true,
      isInactiveDay: true,
      isNoAvailableDays: true,
      cutoffTime: this.cutoffTime
    };
  }

  /**
   * Calculate time difference between two dates
   */
  getTimeDifference(from, to) {
    let diff = to - from;

    if (diff < 0) {
      diff = 0;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { hours, minutes, seconds };
  }

  /**
   * Format date for display
   */
  getFormattedDate(date) {
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  }

  /**
   * Check if product variant is in stock
   */
  isProductInStock() {
    if (!this.variantAvailableEl) {
      return true; // Assume in stock if no element
    }

    const available = this.variantAvailableEl.getAttribute('data-variant-available');
    return available !== 'false';
  }

  /**
   * Update the display based on current time and stock
   */
  updateDisplay() {
    if (!this.countdownDisplay || !this.unavailableMessage) {
      console.warn('NextDayDeliveryTimer.updateDisplay: Missing DOM elements');
      return;
    }

    const inStock = this.isProductInStock();
    const timeData = this.calculateTimeUntilCutoff();
    //const shouldShowTimer = (inStock || this.showWhenOutOfStock) && !timeData.isPastCutoff && !timeData.isInactiveDay && !timeData.isNoAvailableDays;
    const shouldShowTimer =
  (inStock || this.showWhenOutOfStock) &&
  !timeData.isNoAvailableDays;
    // Debug logging
    console.log('NextDayDeliveryTimer.updateDisplay():', {
      inStock,
      showWhenOutOfStock: this.showWhenOutOfStock,
      isPastCutoff: timeData.isPastCutoff,
      isInactiveDay: timeData.isInactiveDay,
      isNoAvailableDays: timeData.isNoAvailableDays,
      shouldShowTimer,
      hours: timeData.hours,
      minutes: timeData.minutes,
      seconds: timeData.seconds
    });

    // Update countdown numbers
    if (this.hoursEl) {
      this.hoursEl.textContent = String(timeData.hours).padStart(2, '0');
    }
    if (this.minutesEl) {
      this.minutesEl.textContent = String(timeData.minutes).padStart(2, '0');
    }
    if (this.secondsEl) {
      this.secondsEl.textContent = String(timeData.seconds).padStart(2, '0');
    }

    // Update cutoff message
    if (this.cutoffTextEl) {
      const message = this.cutoffMessage.replace('{time}', timeData.cutoffTime);
      this.cutoffTextEl.textContent = message;
    }

    // Handle visibility - only ONE state should be visible at a time
    if (shouldShowTimer) {
      console.log('Showing countdown timer');
      this.countdownDisplay.style.display = '';
      this.unavailableMessage.style.display = 'none';
      this.isVisible = true;
      return;
    }

    // If not in stock and we're not showing when out of stock
    if (!inStock && !this.showWhenOutOfStock) {
      console.log('Hiding because product is out of stock');
      this.countdownDisplay.style.display = 'none';
      this.unavailableMessage.style.display = 'none';
      this.isVisible = false;
      return;
    }
   // alert(timeData.isInactiveDay);
    // Past cutoff or inactive day - show unavailable message
    if (timeData.isPastCutoff || timeData.isInactiveDay || timeData.isNoAvailableDays) {
      console.log('Showing unavailable message');
      this.countdownDisplay.style.display = 'none';
      this.unavailableMessage.style.display = '';
      this.isVisible = false;
      return;
    }

    // Default: show timer
    console.log('Default: showing timer');
    this.countdownDisplay.style.display = '';
    this.unavailableMessage.style.display = 'none';
    this.isVisible = true;
  }

  /**
   * Show countdown display
   */
  show() {
    if (this.countdownDisplay) {
      this.countdownDisplay.style.display = '';
    }
    if (this.unavailableMessage) {
      this.unavailableMessage.style.display = 'none';
    }
  }

  /**
   * Hide countdown display
   */
  hide() {
    if (this.countdownDisplay) {
      this.countdownDisplay.style.display = 'none';
    }
    if (this.unavailableMessage) {
      this.unavailableMessage.style.display = '';
    }
  }

  /**
   * Start the countdown timer
   */
  startCountdown() {
    // Update immediately
    this.updateDisplay();

    // Update every second
    this.updateInterval = setInterval(() => {
      this.updateDisplay();
    }, 1000);
  }

  /**
   * Stop the countdown timer
   */
  stopCountdown() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Setup listener for variant changes
   */
  setupVariantListener() {
    this.handleVariantChangeBound = this.onVariantChange.bind(this);

    const section = this.closest('[data-section-id]');
    
    if (section) {
      console.log('NextDayDeliveryTimer: Section found, attaching listeners');
      this.sectionVariantListener = section;
      section.addEventListener('variantChange', this.handleVariantChangeBound);
    } else {
      console.log('NextDayDeliveryTimer: No section with [data-section-id] found');
    }

    // Always attach document listener as fallback
    document.addEventListener('variant:change', this.handleVariantChangeBound);
  }

  /**
   * Handle variant change
   */
  onVariantChange(event) {
    const detail = event.detail || {};
    const variant = detail.variant || null;

    if (variant) {
      if (this.variantAvailableEl) {
        this.variantAvailableEl.setAttribute('data-variant-available', String(variant.available));
      }
    }

    this.updateDisplay();
  }

  /**
   * Remove variant listener
   */
  removeVariantListener() {
    if (this.handleVariantChangeBound) {
      const section = this.sectionVariantListener || this.closest('[data-section-id]');
      if (section) {
        section.removeEventListener('variantChange', this.handleVariantChangeBound);
      }
      document.removeEventListener('variant:change', this.handleVariantChangeBound);
    }
  }
}

// Register the custom element
if (!customElements.get('next-day-delivery-timer')) {
  customElements.define('next-day-delivery-timer', NextDayDeliveryTimer);
}

/**
 * Initialization hook for Shopify theme
 * Ensures the component is available when page loads
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Component will auto-initialize via connectedCallback
  });
} else {
  // Component will auto-initialize via connectedCallback
}
